import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../../lib/api';

const ACTION_LABELS = {
  CREATE: 'Crea',
  UPDATE: 'Aggiorna',
  DISABLE: 'Disattiva',
  ARCHIVE: 'Archivia',
  SKIP: 'Salta',
};

const SummaryChip = ({ label, value, color }) => (
  <div className={`px-3 py-2 rounded-lg text-center ${color}`}>
    <p className="text-xl font-bold leading-none">{value}</p>
    <p className="text-xs mt-1">{label}</p>
  </div>
);

/**
 * Wizard import Excel: 1) upload -> 2) anteprima validazione -> 3) conferma + risultato
 */
export const ExcelImportDialog = ({ open, onOpenChange, onImported }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setResult(null); setOnlyProblems(false);
  };

  const handleClose = (value) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const handleFileSelected = async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setLoading(true);
    try {
      const response = await storeApi.previewExcelImport(selectedFile);
      setPreview(response.data);
      setStep(2);
    } catch (error) {
      toast.error(formatApiError(error));
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    setLoading(true);
    try {
      const response = await storeApi.importExcel(file);
      setResult(response.data);
      setStep(3);
      toast.success('Importazione completata');
      onImported?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const rows = preview?.rows || [];
  const visibleRows = onlyProblems ? rows.filter((r) => !r.valid || r.warnings.length > 0) : rows;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="excel-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            Importa Prodotti da Excel
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Carica il file Excel compilato secondo il modello (foglio "Prodotti").'}
            {step === 2 && 'Anteprima e validazione: controlla gli errori prima di confermare. Le righe con errori NON verranno importate.'}
            {step === 3 && 'Risultato importazione.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {/* STEP 1: UPLOAD */}
          {step === 1 && (
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="excel-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={(e) => handleFileSelected(e.target.files?.[0])}
                data-testid="excel-file-input"
              />
              {loading ? (
                <Loader2 className="h-12 w-12 mx-auto text-blue-500 animate-spin" />
              ) : (
                <Upload className="h-12 w-12 mx-auto text-gray-400" />
              )}
              <p className="mt-4 font-medium">{loading ? 'Validazione in corso...' : 'Clicca per selezionare il file Excel'}</p>
              <p className="text-sm text-muted-foreground mt-1">Formato .xlsx — usa il modello scaricabile per la struttura corretta</p>
            </div>
          )}

          {/* STEP 2: ANTEPRIMA */}
          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-7 gap-2">
                <SummaryChip label="Righe" value={preview.summary.total} color="bg-slate-100 text-slate-700" />
                <SummaryChip label="Da creare" value={preview.summary.create} color="bg-green-100 text-green-700" />
                <SummaryChip label="Da aggiornare" value={preview.summary.update} color="bg-blue-100 text-blue-700" />
                <SummaryChip label="Da disattivare" value={preview.summary.disable} color="bg-gray-200 text-gray-700" />
                <SummaryChip label="Da archiviare" value={preview.summary.archive} color="bg-orange-100 text-orange-700" />
                <SummaryChip label="Warning" value={preview.summary.warnings} color="bg-amber-100 text-amber-700" />
                <SummaryChip label="Errori" value={preview.summary.errors} color="bg-red-100 text-red-700" />
              </div>

              {preview.summary.errors > 0 && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {preview.summary.errors} righe contengono errori e verranno scartate. Correggi il file e ricaricalo per importarle.
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{visibleRows.length} righe mostrate</p>
                <Button variant="outline" size="sm" onClick={() => setOnlyProblems(!onlyProblems)} data-testid="toggle-problems-btn">
                  {onlyProblems ? 'Mostra tutte' : 'Solo errori/warning'}
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-2">Riga</th>
                      <th className="px-3 py-2">Codice</th>
                      <th className="px-3 py-2">Azione</th>
                      <th className="px-3 py-2">Esito</th>
                      <th className="px-3 py-2">Dettagli</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row.row_number} className="border-t" data-testid={`import-row-${row.row_number}`}>
                        <td className="px-3 py-2 text-muted-foreground">{row.row_number}</td>
                        <td className="px-3 py-2 font-mono text-xs">{row.product_code || '-'}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-xs">{ACTION_LABELS[row.resolved_action] || row.resolved_action}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          {!row.valid ? (
                            <Badge className="bg-red-100 text-red-700 gap-1"><XCircle className="h-3 w-3" /> Errore</Badge>
                          ) : row.warnings.length > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 gap-1"><AlertTriangle className="h-3 w-3" /> Warning</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle2 className="h-3 w-3" /> OK</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.errors.map((e, i) => <p key={`err-${i}-${e.slice(0, 24)}`} className="text-red-600">{e}</p>)}
                          {row.warnings.map((w, i) => <p key={`warn-${i}-${w.slice(0, 24)}`} className="text-amber-600">{w}</p>)}
                          {row.valid && row.errors.length === 0 && row.warnings.length === 0 && row.current && (
                            <span className="text-muted-foreground">{row.current.brand} {row.current.model} — € {row.current.base_price}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3: RISULTATO */}
          {step === 3 && result && (
            <div className="space-y-4 text-center py-6">
              <CheckCircle2 className="h-16 w-16 mx-auto text-green-500" />
              <h3 className="text-xl font-bold">Importazione completata</h3>
              <div className="grid grid-cols-5 gap-2 max-w-2xl mx-auto">
                <SummaryChip label="Creati" value={result.summary.created} color="bg-green-100 text-green-700" />
                <SummaryChip label="Aggiornati" value={result.summary.updated} color="bg-blue-100 text-blue-700" />
                <SummaryChip label="Disattivati" value={result.summary.disabled} color="bg-gray-200 text-gray-700" />
                <SummaryChip label="Archiviati" value={result.summary.archived} color="bg-orange-100 text-orange-700" />
                <SummaryChip label="Scartati" value={result.summary.errors + result.summary.skipped} color="bg-red-100 text-red-700" />
              </div>
              <p className="text-xs text-muted-foreground">Batch ID: {result.batch_id} — tutte le modifiche sono tracciate nell'audit log</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => { setStep(1); setPreview(null); setFile(null); }}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Cambia file
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={loading || preview?.summary?.valid === 0}
                data-testid="confirm-import-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Conferma Importazione ({preview?.summary?.valid} righe valide)
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={() => handleClose(false)} data-testid="close-import-btn">Chiudi</Button>
          )}
          {step === 1 && (
            <Button variant="outline" onClick={() => handleClose(false)}>Annulla</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
